import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;

export const cycleCountService = {
    /**
     * Fetch all cycle counts with pagination and filters
     */
    async getAll(page = 1, pageSize = 20, sortBy = 'createdAt', sortDirection = 'desc', filters = {}) {
        const params = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
            sortBy,
            sortDirection,
        });

        if (filters.query) {
            params.append('query', filters.query);
        }
        if (filters.status && filters.status.length > 0) {
            params.append('status', filters.status.join(','));
        }
        if (filters.type && filters.type.length > 0) {
            params.append('type', filters.type.join(','));
        }
        if (filters.location) {
            params.append('location', filters.location);
        }
        if (filters.UTCOffset !== undefined) {
            params.append('UTCOffset', filters.UTCOffset.toString());
        }
        if (filters.createdDateFrom) {
            params.append('createdDateFrom', filters.createdDateFrom);
        }
        if (filters.createdDateTo) {
            params.append('createdDateTo', filters.createdDateTo);
        }
        if (filters.updatedDateFrom) {
            params.append('updatedDateFrom', filters.updatedDateFrom);
        }
        if (filters.updatedDateTo) {
            params.append('updatedDateTo', filters.updatedDateTo);
        }

        const response = await axios.get(`${API_URL}/api/app/cycle-counts/all?${params}`);
        return response.data;
    },

    /**
     * Create a new cycle count
     */
    async create(data) {
        const response = await axios.post(`${API_URL}/api/app/cycle-counts/create`, data);
        return response.data;
    },

    /**
     * Fetch cycle count by ID (details only, no items)
     */
    async getById(id) {
        const response = await axios.get(`${API_URL}/api/app/cycle-counts/${id}`);
        return response.data;
    },

    /**
     * Fetch paginated cycle count items
     */
    async getItems(id, page = 1, pageSize = 12, sortBy = 'sku', sortDirection = 'asc') {
        const response = await axios.get(`${API_URL}/api/app/cycle-counts/${id}/items?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&sortDirection=${sortDirection}`);
        return response.data;
    },

    /**
     * Fetch cycle count details for performing the count
     */
    async getPerformDetails(id) {
        const response = await axios.get(`${API_URL}/api/app/cycle-counts/${id}/perform`);
        return response.data;
    },

    /**
     * Fetch cycle count status
     */
    async getStatus(id) {
        const response = await axios.get(`${API_URL}/api/app/cycle-counts/${id}/status`);
        return response.data;
    },

    /**
     * Update cycle count status
     */
    async updateStatus(id, status) {
        const response = await axios.patch(`${API_URL}/api/app/cycle-counts/${id}/status`, { status });
        return response.data;
    },

    /**
     * Create a new batch for a cycle count
     */
    async createBatch(cycleCountId, data) {
        const response = await axios.post(`${API_URL}/api/app/cycle-counts/${cycleCountId}/batches`, data);
        return response.data;
    },

    /**
     * Update batch items
     */
    async updateBatch(cycleCountId, batchId, data) {
        const response = await axios.put(`${API_URL}/api/app/cycle-counts/${cycleCountId}/batches/${batchId}`, data);
        return response.data;
    },

    /**
     * Update batch description
     */
    async updateBatchDescription(cycleCountId, batchId, description) {
        const response = await axios.patch(`${API_URL}/api/app/cycle-counts/${cycleCountId}/batches/${batchId}/description`, {
            description,
        });
        return response.data;
    },

    /**
     * Upload CSV file to batch
     */
    async uploadCsvToBatch(cycleCountId, batchId, file) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API_URL}/api/app/cycle-counts/${cycleCountId}/batches/${batchId}/upload-csv`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    /**
     * Get paginated batch items
     */
    async getBatchItems(cycleCountId, batchId, page = 1, pageSize = 50, options = {}) {
        const params = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
        });

        if (options.sortBy) {
            params.append('sortBy', options.sortBy);
        }

        if (options.sortDirection) {
            params.append('sortDirection', options.sortDirection);
        }

        if (options.query) {
            params.append('query', options.query);
        }

        if (options.sku) {
            params.append('sku', options.sku);
        }

        if (options.quantityMin !== undefined && options.quantityMin !== null && options.quantityMin !== '') {
            params.append('quantityMin', options.quantityMin.toString());
        }

        if (options.quantityMax !== undefined && options.quantityMax !== null && options.quantityMax !== '') {
            params.append('quantityMax', options.quantityMax.toString());
        }

        const response = await axios.get(`${API_URL}/api/app/cycle-counts/${cycleCountId}/batches/${batchId}/items?${params.toString()}`);
        return response.data;
    },

    /**
     * Trigger starting inventory sync for a cycle count
     */
    async startCount(cycleCountId) {
        const response = await axios.patch(`${API_URL}/api/app/cycle-counts/${cycleCountId}/start-count`);
        return response.data;
    },

    /**
     * Submit cycle count for manager approval
     */
    async submitForApproval(cycleCountId) {
        const response = await axios.patch(`${API_URL}/api/app/cycle-counts/${cycleCountId}/submit-for-approval`);
        return response.data;
    },

    /**
     * Approve cycle count (manager or finance based on status)
     */
    async approve(cycleCountId) {
        const response = await axios.patch(`${API_URL}/api/app/cycle-counts/${cycleCountId}/approve`);
        return response.data;
    },

    /**
     * Resume a failed inventory push for an approved cycle count
     */
    async retryInventorySync(cycleCountId) {
        const response = await axios.patch(`${API_URL}/api/app/cycle-counts/${cycleCountId}/retry-inventory-sync`);
        return response.data;
    },

    /**
     * Reject cycle count with reason
     */
    async reject(cycleCountId, rejectionReason) {
        const response = await axios.patch(`${API_URL}/api/app/cycle-counts/${cycleCountId}/reject`, {
            rejectionReason,
        });
        return response.data;
    },

    /**
     * Export cycle count data as CSV file
     */
    async export(cycleCountId) {
        const response = await axios.post(
            `${API_URL}/api/app/cycle-counts/${cycleCountId}/export`,
            {},
            {
                responseType: 'blob',
            }
        );
        return response;
    },

    /**
     * Get reason codes for cycle counts
     */
    async getReasonCodes() {
        const response = await axios.get(`${API_URL}/api/app/cycle-counts/reason-codes`);
        return response.data;
    },
};
